import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { MessageCircle, Phone, Menu, X, ArrowUp } from "lucide-react";

const SECTIONS = [
  { id: "story", label: "About Andrea" },
  { id: "menu", label: "The Menu" },
  { id: "details", label: "Practical Details" },
  { id: "inquire", label: "Request a Seat" },
];

/** A thin gold progress bar at the top of the page — gives a clear "where am I"
 *  signal for ADHD scanning without any extra UI weight. */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 24 });

  return (
    <motion.div
      data-testid="scroll-progress"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-[color:var(--gold)] origin-left z-[60]"
      aria-hidden="true"
    />
  );
}

/** Always-accessible WhatsApp + back-to-top floating actions.
 *  - WhatsApp pulses gently on first reveal to signal "primary action".
 *  - Back-to-top appears only after the user has scrolled past the hero. */
export function FloatingActions() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Empty deps are intentional: onScroll is defined inside the effect and
    // setScrolled (useState setter) is stable per React docs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 z-50 flex flex-col items-end gap-3">
      {scrolled && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          className="w-11 h-11 rounded-full bg-[color:var(--paper)] border border-[color:var(--rule)] text-[color:var(--ink-2)] shadow-md flex items-center justify-center hover:text-[color:var(--moss)] transition-colors"
          data-testid="back-to-top-button"
        >
          <ArrowUp size={18} strokeWidth={1.6} />
        </button>
      )}
      <a
        href="https://wa.me/40746264632"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp Andrea"
        className="w-14 h-14 rounded-full bg-[color:var(--moss)] text-[color:var(--paper)] shadow-lg flex items-center justify-center hover:bg-[color:var(--moss-dark)] transition-colors ring-4 ring-[color:var(--moss)]/15"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle size={22} strokeWidth={1.6} />
      </a>
    </div>
  );
}

/** Mobile-only jump menu (≤ md). Plain, calm drawer — no flashy animation. */
export function MobileJumpMenu() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="sm:hidden fixed top-4 right-4 z-[55] w-11 h-11 rounded-full bg-[color:var(--paper)]/90 backdrop-blur border border-[color:var(--rule)] text-[color:var(--ink)] shadow-md flex items-center justify-center"
        data-testid="mobile-menu-button"
      >
        <Menu size={20} strokeWidth={1.6} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-[color:var(--paper)] flex flex-col"
          data-testid="mobile-menu-drawer"
        >
          <div className="flex items-center justify-between px-6 pt-5">
            <p className="font-display italic text-lg">Andrea · Cluj</p>
            <button
              type="button"
              onClick={close}
              aria-label="Close menu"
              className="w-11 h-11 rounded-full border border-[color:var(--rule)] flex items-center justify-center"
              data-testid="mobile-menu-close"
            >
              <X size={20} strokeWidth={1.6} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col justify-center px-6 -mt-10">
            <ol className="space-y-5">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={close}
                    className="flex items-baseline gap-4 group"
                    data-testid={`mobile-menu-link-${s.id}`}
                  >
                    <span className="font-display italic text-[color:var(--gold)] text-2xl w-10">
                      0{i + 1}
                    </span>
                    <span className="font-display text-4xl text-[color:var(--ink)] group-hover:text-[color:var(--moss)] transition-colors">
                      {s.label}
                    </span>
                  </a>
                </li>
              ))}
            </ol>

            <div className="mt-12 space-y-3">
              <a
                href="https://wa.me/40746264632"
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="btn-primary w-full"
                data-testid="mobile-menu-whatsapp"
              >
                <MessageCircle size={16} strokeWidth={1.6} className="mr-2" />
                WhatsApp Andrea
              </a>
              <a
                href="tel:+40746264632"
                onClick={close}
                className="btn-ghost w-full justify-center"
                data-testid="mobile-menu-call"
              >
                <Phone size={14} strokeWidth={1.6} />
                Call +40 746 264 632
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
